/**
 *  @class
 *  @function ProductComparePanel
 */
if (!customElements.get('product-compare-panel')) {
	class ProductComparePanel extends HTMLElement {
		/** @type {HTMLElement | null} */
		toggle;
		/** @type {HTMLElement | null} */
		drawer;
		/** @type {HTMLElement | null} */
		clear;
		/** @type {HTMLButtonElement | null} */
		start;
		/** @type {HTMLElement | null} */
		modal;
		/** @type {HTMLElement | null} */
		table;
		/** @type {number} */
		max;
		/** @type {string} */
		href;

		constructor() {
			super();
			this.toggle = this.querySelector('.compare-toggle');
			this.drawer = this.querySelector('.compare-drawer');
			this.clear = this.querySelector('.compare-clear');
			this.start = this.querySelector('.compare-start');
			this.modal = this.querySelector('.product-compare-modal');
			this.table = this.querySelector('product-compare-table');
			this.max = Number(this.dataset.max) || 0;
			this.href = '';
		}

		connectedCallback() {
			// Add compare-true class to body only if compare_toggle is checked
			const compareToggle = document.getElementById('compare_toggle');
			if (compareToggle) {
				const checkbox = compareToggle;
				if (checkbox.checked) {
					document.body.classList.add('compare-true');
				}
			}
			if (this.toggle) {
				setTimeout(() => this.toggle.classList.add('compare-toggle--active'), 250);
			}
			this.addEventListeners();
			this.updateCompareCheckboxes();
			this.updateCompareCounters();
			this.updateStartButton();
		}

		addEventListeners() {
			if (this.toggle) {
				this.toggle.addEventListener('click', this.onButtonClick.bind(this));
			}
			if (this.drawer) {
				this.drawer.addEventListener('click', this.onRemoveClick.bind(this));
			}
			if (this.clear) {
				this.clear.addEventListener('click', this.onClearClick.bind(this));
			}
			if (this.start) {
				this.start.addEventListener('click', this.onStartClick.bind(this));
			}
			document.addEventListener('change', this.onCheckboxClick.bind(this));
		}

		/**
		 * @returns {Array<{id: string, url: string, handle: string}>}
		 */
		getSelectedProducts() {
			const stored = localStorage.getItem('compare-products-vision');
			return stored ? JSON.parse(stored) : [];
		}

		/**
		 * @param {string} productId
		 * @param {string} productUrl
		 * @param {string} productHandle
		 */
		addToCompare(productId, productUrl, productHandle) {
			const compareProducts = this.getSelectedProducts();
			if (!compareProducts.some(product => product.id === productId)) {
				compareProducts.push({ id: productId, url: productUrl, handle: productHandle });
				localStorage.setItem('compare-products-vision', JSON.stringify(compareProducts));
				this.updateCompareComponents();
			}
		}

		/**
		 * @param {string} productId
		 */
		removeFromCompare(productId) {
			const compareProducts = this.getSelectedProducts().filter(product => product.id !== productId);
			localStorage.setItem('compare-products-vision', JSON.stringify(compareProducts));
			this.updateCompareComponents();
		}

		updateStartButton() {
			if (this.start) {
				this.start.disabled = !this.getCompareCount();
			}
		}

		/**
		 * @param {Event} event
		 */
		onCheckboxClick(event) {
			const target = event.target;
			if (target && target.classList.contains('product-card-compare--checkbox')) {
				const checkbox = target;
				if (checkbox && checkbox.checked && checkbox.dataset) {
					const { productId, productUrl, productHandle } = checkbox.dataset;
					if (productId && productUrl && productHandle) {
						if (this.getCompareCount() < this.max) {
							this.addToCompare(productId, productUrl, productHandle);
						}
					}
				} else if (checkbox && !checkbox.checked && checkbox.dataset && checkbox.dataset.productId) {
					this.removeFromCompare(checkbox.dataset.productId);
				}
			}
		}

		updateCompareCheckboxes() {
			const compareProducts = this.getSelectedProducts();
			const compareCount = this.getCompareCount();
			document.querySelectorAll('.product-card-compare--checkbox').forEach(checkbox => {
				if (checkbox && checkbox.dataset && checkbox.dataset.productId) {
					const isChecked = compareProducts.some(product => product && product.id === checkbox.dataset.productId);
					checkbox.checked = isChecked;
					checkbox.disabled = !isChecked && compareCount >= this.max;
				}
			});
		}

		/**
		 * @param {Event} event
		 */
		onRemoveClick(event) {
			const target = event.target;
			if (target && target.classList.contains('compare-remove') && target.dataset && target.dataset.productId) {
				const productId = target.dataset.productId;
				this.removeFromCompare(productId);
			}
		}

		onClearClick() {
			const compareProducts = this.getSelectedProducts();
			if (compareProducts && Array.isArray(compareProducts)) {
				compareProducts.forEach(product => {
					if (product && product.id) {
						this.removeFromCompare(product.id);
					}
				});
			}
			if (this.drawer) {
				const list = this.drawer.querySelector('.compare-drawer--list');
				if (list) {
					list.innerHTML = '<div class="compare-drawer--list-item"></div>'.repeat(this.max);
				}
			}
			this.closeDrawer();
		}

		onStartClick() {
			if (this.modal && typeof this.modal.show === 'function') {
				this.modal.show();
			}
			if (this.table) {
				this.table.dispatchEvent(new CustomEvent('render-product-compare-modal', { bubbles: true }));
			}
		}

		openDrawer() {
			if (this.drawer) {
				this.drawer.removeAttribute('inert');
				if (typeof this.drawer.focus === 'function') {
					this.drawer.focus();
				}
				this.drawer.classList.add('active');
				document.body.classList.add('open-cc');
			}
		}

		closeDrawer() {
			if (this.drawer) {
				this.drawer.setAttribute('inert', '');
				if (typeof this.drawer.blur === 'function') {
					this.drawer.blur();
				}
				this.drawer.classList.remove('active');
				document.body.classList.remove('open-cc');
			}
		}

		onButtonClick() {
			this.renderProducts();
			this.openDrawer();
		}

		getCompareCount() {
			const products = this.getSelectedProducts();
			return products ? products.length : 0;
		}

		updateCompareCounters() {
			const count = this.getCompareCount();
			document.querySelectorAll('.compare-counter').forEach(counter => {
				if (counter) {
					counter.textContent = count.toString();
				}
			});
		}

		async renderProducts() {
			const compareProducts = this.getSelectedProducts();
			if (!compareProducts || !Array.isArray(compareProducts)) {
				return;
			}
			/** @type {string[]} */
			const returnArray = await Promise.all(compareProducts.map(async (product) => {
				try {
					if (!product || !product.handle) {
						throw new Error('Invalid product data');
					}
					const locale = window.theme ? window.theme.locale : { iso_code: 'de' };
					const isEnglish = locale.iso_code === 'en';
					const apiUrl = isEnglish ? `/en/products/${encodeURIComponent(product.handle)}.js` : `/products/${encodeURIComponent(product.handle)}.js`;
					const response = await fetch(apiUrl);
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					const productData = await response.json();
					const sku = productData.variants && productData.variants.length > 0 ? productData.variants[0].sku : '';
					const naText = window.theme && window.theme.strings && window.theme.strings.compare ? window.theme.strings.compare.na : 'N/A';

					const productUrl = isEnglish ?
						`/en/products/${encodeURIComponent(productData.handle)}` :
						`/products/${encodeURIComponent(productData.handle)}`;
					return `
						<div class="product-card-small">
							<div class="product-card-small--image">
								<a href="${productUrl}" title="${productData.title}" class="product-cart-small--image-link">
									<img src="${productData.images && productData.images.length > 0 ? productData.images[0] : ''}" alt="${productData.title}" style="width: 70px; height: 80px; object-fit: cover;">
								</a>
							</div>
							<div class="product-card-small--info">
								<a href="${productUrl}" title="${productData.title}" class="product-card-small--link">${productData.title}</a>
								<div class="product-card-small--add-to-cart">
									<div class="product-price">
										<span class="money">${sku || naText}</span>
									</div>
								</div>
								<button class="compare-remove" data-product-id="${productData.id}">&times;</button>
							</div>
						</div>
					`;
				} catch (error) {
					console.error('Error fetching product data:', error);
					const errorText = window.theme && window.theme.strings && window.theme.strings.compare ? window.theme.strings.compare.errorLoadingProduct : 'Fehler beim Laden des Produkts';
					return `<div class="product-card-small"><div class="product-card-small--info">${errorText}</div></div>`;
				}
			}));

			if (this.drawer) {
				const list = this.drawer.querySelector('.compare-drawer--list');
				if (list) {
					const returnHtml = returnArray.join('') + '<div class="compare-drawer--list-item"></div>'.repeat(this.max - compareProducts.length);
					list.innerHTML = returnHtml;
				}
			}
		}

		/**
		 * @param {string} html
		 * @param {string} selector
		 * @returns {string}
		 */
		getSectionInnerHTML(html, selector) {
			if (!html) {
				return '';
			}
			const element = new DOMParser()
				.parseFromString(html, 'text/html')
				.querySelector(selector);
			return element ? element.innerHTML : '';
		}

		updateCompareComponents() {
			this.updateCompareCheckboxes();
			this.updateCompareCounters();
			this.updateStartButton();
			this.renderProducts();
		}
	}

	customElements.define('product-compare-panel', ProductComparePanel);
}